import type { PillTone } from '@/features/deal-review/review-tones'
import type {
  ActiveSendBatches,
  SendBatch,
  SendBatchEnqueue,
  SendBatchItemStatus,
  SendBatchPreview,
  SendBatchSkipReason,
} from '@/types/api'

export type PillState =
  | 'sending'
  | 'sending-queued'
  | 'queued'
  | 'finished'
  | 'failures'
  | 'stopped'

export interface PillView {
  state: PillState
  label: string
  tone: PillTone
  batch: SendBatch
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)
const deals = (n: number) => `${n} ${plural(n, 'deal', 'deals')}`

// Formatting only: every count here comes from the backend.
export function sendStatusPill(
  active: ActiveSendBatches,
  dismissedIds: string[]
): PillView | null {
  const { running, queued, lastFinished } = active
  if (running) {
    const label = `Sending ${running.done} of ${running.total}`
    return queued.length > 0
      ? {
          state: 'sending-queued',
          label: `${label} · ${queued.length} queued`,
          tone: 'busy',
          batch: running,
        }
      : { state: 'sending', label, tone: 'busy', batch: running }
  }
  if (queued.length > 0) {
    return {
      state: 'queued',
      label: `${queued.length} queued`,
      tone: 'busy',
      batch: queued[0],
    }
  }
  if (!lastFinished || dismissedIds.includes(lastFinished.id)) return null

  const { total, sent, failed, duplicates, skipped } = lastFinished
  if (lastFinished.status === 'INTERRUPTED') {
    return {
      state: 'stopped',
      // done also counts the SKIPPED items the stop never reached.
      label: `Stopped at ${total - skipped} of ${total}`,
      tone: 'warning',
      batch: lastFinished,
    }
  }
  if (failed > 0) {
    return {
      state: 'failures',
      label: `${sent} sent · ${failed} failed`,
      tone: 'warning',
      batch: lastFinished,
    }
  }
  return {
    state: 'finished',
    label:
      duplicates > 0
        ? `${sent} sent · ${duplicates} set aside`
        : `${sent} sent`,
    tone: 'default',
    batch: lastFinished,
  }
}

const SKIP_ORDER: SendBatchSkipReason[] = [
  'SENT',
  'IN_PROGRESS',
  'QUEUED_ELSEWHERE',
  'STALE',
  'NOT_SENDABLE',
]

export function skipReasonLine(
  reason: SendBatchSkipReason,
  k: number,
  message?: string | null
): string {
  switch (reason) {
    case 'SENT':
      return `${k} already sent`
    case 'IN_PROGRESS':
      return `${k} being sent now`
    case 'QUEUED_ELSEWHERE':
      return `${k} already queued in another send`
    case 'STALE':
      return `${k} changed since the list loaded. Refresh and select ${plural(k, 'it', 'them')} again.`
    case 'NOT_SENDABLE':
      return message
        ? `${k} cannot be sent yet (${message})`
        : `${k} cannot be sent yet`
  }
}

function skipLines(
  skipped: SendBatchPreview['skipped'],
  line: typeof skipReasonLine
): string[] {
  return SKIP_ORDER.flatMap((reason) => {
    const items = skipped.filter((item) => item.reason === reason)
    if (items.length === 0) return []
    const messages = [
      ...new Set(items.flatMap((item) => (item.message ? [item.message] : []))),
    ]
    return [line(reason, items.length, messages.join('; '))]
  })
}

export interface ConfirmCopy {
  title: string
  /** Null when the title says it all. */
  description: string | null
  /** Null when there is nothing to send: the dialog shows only Close. */
  action: string | null
  impliedLine: string | null
  queueLine: string | null
  skippedIntro: string | null
  skippedLines: string[]
}

export function confirmCopy(
  preview: SendBatchPreview,
  selectedCount: number,
  anotherRunning: boolean
): ConfirmCopy {
  const { accepted, recordsDecision, skipped } = preview
  const skippedLines = skipLines(skipped, skipReasonLine)
  // NOT_SENDABLE is the backend's "needs a reviewer" skip; only then does the decision line matter.
  const needsReview = skipped.some((item) => item.reason === 'NOT_SENDABLE')

  if (accepted === 0) {
    return {
      title: 'Nothing to send',
      description: `None of the ${selectedCount} selected can be sent.`,
      action: null,
      impliedLine: null,
      queueLine: null,
      skippedIntro: null,
      skippedLines,
    }
  }

  return {
    title: `Send ${deals(accepted)} to DealCloud?`,
    description: null,
    action: anotherRunning ? `Queue ${accepted}` : `Send ${accepted}`,
    impliedLine:
      needsReview && recordsDecision > 0
        ? `${recordsDecision} of them ${plural(recordsDecision, 'has', 'have')} no decision yet. Sending records screening's recommended decision for ${plural(recordsDecision, 'it', 'them')}.`
        : null,
    queueLine: anotherRunning
      ? 'Another send is running. These start when it finishes.'
      : null,
    // A skip is why the dialog opened at all (see isUndoableSend), so it is always listed.
    skippedIntro:
      skipped.length > 0
        ? `${skipped.length} of the ${selectedCount} selected will not be sent:`
        : null,
    skippedLines,
  }
}

/** How long the bar's "Sending N…" toast offers Undo before the batch is posted. */
export const SEND_UNDO_MS = 5_000

/** Bottom-right: the app's Toaster is bottom-center, where the selection bar sits. */
export const SEND_TOAST_POSITION = 'bottom-right'

/** "DealCloud: 10 sent · 2 failed", the finished pill's own label. */
export function completionMessage(view: PillView): string {
  const details =
    view.state === 'finished' ? '' : '. Details in the DealCloud status.'
  return `DealCloud: ${view.label}${details}`
}

export const ITEM_STATUS_LABEL: Record<SendBatchItemStatus, string> = {
  QUEUED: 'Queued',
  RUNNING: 'Sending',
  SENT: 'Sent',
  FAILED: 'Failed',
  DUPLICATE: 'Set aside',
  SKIPPED: 'Skipped',
}

/** Nothing to warn about: every selected lead is accepted, so the bar skips the dialog. */
export function isUndoableSend(preview: SendBatchPreview): boolean {
  return preview.accepted > 0 && preview.skipped.length === 0
}

const BACKGROUND_NOTE =
  'They send one at a time in the background. You can leave; progress shows in the DealCloud status.'

export function queuedMessage(
  result: SendBatchEnqueue,
  preview: SendBatchPreview | undefined
): string {
  const base = `${deals(result.accepted)} queued for DealCloud.`
  const extra = result.skipped.length - (preview?.skipped.length ?? 0)
  const skippedNote =
    extra > 0
      ? ` ${extra} changed in the meantime and ${plural(extra, 'was', 'were')} skipped.`
      : ''
  return `${base}${skippedNote} ${BACKGROUND_NOTE}`
}

// A retry re-checks each lead; one edited since the batch ran needs a fresh confirm.
export function retriedMessage(result: SendBatchEnqueue): string {
  const head =
    result.accepted > 0
      ? `${result.accepted} queued again for DealCloud.`
      : 'Nothing was queued again.'
  const lines = skipLines(result.skipped, (reason, k, message) =>
    reason === 'STALE'
      ? `${k} changed since you confirmed; send ${plural(k, 'it', 'them')} again from the Inbox.`
      : `${skipReasonLine(reason, k, message)}.`
  )
  return [head, ...lines].join(' ')
}

const DISMISSED_KEY = 'deal-review:dismissed-send-batches'

export function dismissedBatchIds(): string[] {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(DISMISSED_KEY) ?? '[]'
    )
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : []
  } catch {
    return []
  }
}

export function dismissBatch(id: string): void {
  try {
    // Only the newest finished batch is ever shown, so older ids need not be kept.
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([id]))
  } catch {
    // Storage blocked: the pill just stays until the next batch.
  }
}
